import type { Skill, SkillsResponse } from 'job-bank-data-access';
import { getAccessToken } from '@accenturecodefoundry/mfe-pot-shared-auth/core';
import { loadRuntimeConfig } from '../../runtime-config';
import { assetBaseUrl } from '../asset-base-url';

// loadRuntimeConfig fetches and parses env.js on every call with no built-in
// cache — memoize so every BFF call in this module shares one fetch.
let configPromise: ReturnType<typeof loadRuntimeConfig> | undefined;
const getConfig = () => (configPromise ??= loadRuntimeConfig(assetBaseUrl));

function toSkillModels(skills: string[]): Skill[] {
  return skills.map((s) => ({ id: s, label: { en: s, fr: s } }));
}

async function bffFetch(path: string, opts?: RequestInit): Promise<Response> {
  const { jobBankBffBaseUrl } = await getConfig();
  const token = getAccessToken();
  const res = await fetch(`${jobBankBffBaseUrl}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...opts?.headers,
    },
  });
  if (!res.ok) {
    const body: { error?: string } = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `BFF error ${res.status}`);
  }
  return res;
}

export async function fetchSkills(): Promise<Skill[]> {
  const res = await bffFetch('/api/v1/skills');
  const { skills } = (await res.json()) as SkillsResponse;
  return toSkillModels(skills);
}

export async function addSkill(label: string): Promise<Skill> {
  const res = await bffFetch('/api/v1/skills', {
    method: 'POST',
    body: JSON.stringify({ skill: label }),
  });
  const { skills } = (await res.json()) as SkillsResponse;
  const added = skills.find((s) => s === label.trim()) ?? label.trim();
  return { id: added, label: { en: added, fr: added } };
}

// The skill's id IS its text — the BFF DELETE path param is the URL-encoded skill text.
export async function removeSkill(id: string): Promise<void> {
  await bffFetch(`/api/v1/skills/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
